import React, { useMemo, useState, useEffect } from 'react';
import {
  RACES, UNITS, STRUCTURES, ECON, unitsByRace,
  mineralIncome, gasIncome, unitsPerMinute, formatNum,
} from './sc2Data';

const DEFAULT_LINES = {
  terran: [
    { unitId: 'marine', count: 3, mode: true },
    { unitId: 'medivac', count: 1, mode: true },
  ],
  protoss: [
    { unitId: 'stalker', count: 3, mode: true },
    { unitId: 'immortal', count: 1, mode: false },
  ],
  zerg: [
    { unitId: 'zergling', count: 12, mode: false },
    { unitId: 'roach', count: 5, mode: false },
  ],
};

// mode meaning:
//  - terran: reactor attached (for reactor-capable units)
//  - protoss: warpgate researched
//  - zerg: unused per line
function lineStats(line, raceId, injectPerHatch) {
  const unit = UNITS[line.unitId];
  if (!unit) return null;
  let rate; // units per minute
  let facilities = line.count;
  if (raceId === 'zerg') {
    rate = line.count; // count = desired units/min
    facilities = null;
  } else {
    const perFacility = unitsPerMinute(unit, {
      reactor: raceId === 'terran' && line.mode,
      warpgate: raceId === 'protoss' && line.mode,
    });
    rate = perFacility * line.count;
  }
  return {
    unit,
    rate,
    facilities,
    minerals: rate * unit.minerals,
    gas: rate * unit.gas,
    supply: rate * unit.supply,
    larvae: raceId === 'zerg' ? rate * (unit.larva || 0) : 0,
  };
}

function ProductionPlanner({ raceId, econ, setEcon }) {
  const race = RACES[raceId];
  const [linesByRace, setLinesByRace] = useState(DEFAULT_LINES);
  const lines = linesByRace[raceId];
  const setLines = (next) => setLinesByRace({ ...linesByRace, [raceId]: next });

  const [addUnitId, setAddUnitId] = useState('');
  useEffect(() => { setAddUnitId(''); }, [raceId]);

  const availableUnits = useMemo(
    () => unitsByRace(raceId).filter((u) => !u.noPlanner),
    [raceId]
  );

  const income = {
    minerals: mineralIncome(econ.mineralWorkers, econ.bases)
      + (raceId === 'terran' ? econ.orbitals * ECON.MULE_PER_MIN : 0),
    gas: gasIncome(econ.gasWorkers, econ.bases),
  };

  const stats = lines.map((l) => lineStats(l, raceId)).filter(Boolean);
  const totals = stats.reduce(
    (acc, s) => ({
      minerals: acc.minerals + s.minerals,
      gas: acc.gas + s.gas,
      supply: acc.supply + s.supply,
      larvae: acc.larvae + s.larvae,
    }),
    { minerals: 0, gas: 0, supply: 0, larvae: 0 }
  );

  // Zerg larvae supply: hatcheries (natural) + queens (inject)
  const larvaeAvailable = econ.bases * ECON.LARVA_NATURAL_PER_MIN
    + Math.min(econ.queens, econ.bases) * ECON.LARVA_INJECT_PER_MIN
    + Math.max(0, econ.queens - econ.bases) * 0; // extra queens beyond hatch count don't inject

  const mineralUse = income.minerals > 0 ? totals.minerals / income.minerals : Infinity;
  const gasUse = income.gas > 0 ? (totals.gas > 0 ? totals.gas / income.gas : 0) : (totals.gas > 0 ? Infinity : 0);
  const larvaeUse = raceId === 'zerg' && larvaeAvailable > 0 ? totals.larvae / larvaeAvailable : 0;

  const worstUse = Math.max(mineralUse, gasUse, raceId === 'zerg' ? larvaeUse : 0);

  const verdict = () => {
    if (stats.length === 0) return { cls: '', text: 'Добавьте юниты, чтобы рассчитать продакшен.' };
    if (worstUse > 1.05) {
      const bottleneck = mineralUse >= gasUse && mineralUse >= larvaeUse ? 'минералов'
        : gasUse >= larvaeUse ? 'газа' : 'личинок';
      return {
        cls: 'bad',
        text: `⛔ Продакшен ПЕРЕГРУЖЕН: не хватает ${bottleneck} (${Math.round(worstUse * 100)}% дохода). Здания будут простаивать или деньги уйдут в минус. Уменьшите продакшен или добавьте рабочих/базы.`,
      };
    }
    if (worstUse >= 0.8) {
      return {
        cls: 'good',
        text: `✅ Оптимально: вы тратите ${Math.round(worstUse * 100)}% дохода. Деньги не копятся, продакшен почти не простаивает.`,
      };
    }
    return {
      cls: 'warn',
      text: `💰 Недогруз: вы тратите только ${Math.round(worstUse * 100)}% дохода. Минералы будут копиться — добавьте производственные здания, юниты или расширяйтесь.`,
    };
  };

  const updateLine = (i, patch) => {
    const next = lines.slice();
    next[i] = { ...next[i], ...patch };
    setLines(next);
  };

  const addLine = () => {
    if (!addUnitId) return;
    if (lines.some((l) => l.unitId === addUnitId)) return;
    setLines([...lines, { unitId: addUnitId, count: raceId === 'zerg' ? 4 : 1, mode: false }]);
    setAddUnitId('');
  };

  const set = (patch) => setEcon({ ...econ, ...patch });
  const v = verdict();

  const facilityName = (unit) => STRUCTURES[unit.producedFrom]?.ru || '';

  return (
    <div>
      <h2 className="sc2-section-title" style={{ color: race.color }}>
        {race.icon} Калькулятор продакшена: {race.ru}
      </h2>

      <div className="sc2-panel sc2-econ-strip">
        <span className="sc2-econ-strip-title">Экономика (из вкладки «Добыча»):</span>
        <label>Базы <input type="number" min="1" max="6" value={econ.bases} onChange={(e) => set({ bases: Number(e.target.value) })} /></label>
        <label>Мин. рабочие <input type="number" min="0" max="90" value={econ.mineralWorkers} onChange={(e) => set({ mineralWorkers: Number(e.target.value) })} /></label>
        <label>Газ. рабочие <input type="number" min="0" max="36" value={econ.gasWorkers} onChange={(e) => set({ gasWorkers: Number(e.target.value) })} /></label>
        {raceId === 'terran' && (
          <label>Орбиталки <input type="number" min="0" max="6" value={econ.orbitals} onChange={(e) => set({ orbitals: Number(e.target.value) })} /></label>
        )}
        {raceId === 'zerg' && (
          <label>Королевы (инжект) <input type="number" min="0" max="8" value={econ.queens} onChange={(e) => set({ queens: Number(e.target.value) })} /></label>
        )}
        <span className="sc2-econ-strip-income">
          Доход: 💎 {formatNum(income.minerals)}/мин · 🟢 {formatNum(income.gas)}/мин
          {raceId === 'zerg' && <> · 🥚 {formatNum(larvaeAvailable, 1)} личинок/мин</>}
        </span>
      </div>

      <div className="sc2-two-col">
        <div className="sc2-panel">
          <h3>Состав армии</h3>
          <table className="sc2-table compact">
            <thead>
              <tr>
                <th>Юнит</th>
                {raceId !== 'zerg' && <th>{raceId === 'terran' ? 'Зданий' : 'Гейтов'}</th>}
                {raceId === 'terran' && <th>Реактор</th>}
                {raceId === 'protoss' && <th>Варп-врата</th>}
                {raceId === 'zerg' && <th>Юнитов/мин</th>}
                <th>Юнитов/мин</th>
                <th>💎/мин</th>
                <th>🟢/мин</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s, i) => (
                <tr key={s.unit.id}>
                  <td>
                    <strong>{s.unit.ru}</strong>
                    <div className="sc2-unit-name-en">{facilityName(s.unit)}{s.unit.techLab ? ' + лаб.' : ''}</div>
                  </td>
                  {raceId !== 'zerg' && (
                    <td>
                      <input
                        className="sc2-count-input"
                        type="number" min="0" max="30"
                        value={lines[i].count}
                        onChange={(e) => updateLine(i, { count: Number(e.target.value) })}
                      />
                    </td>
                  )}
                  {raceId === 'terran' && (
                    <td>
                      {s.unit.reactor ? (
                        <input type="checkbox" checked={lines[i].mode} onChange={(e) => updateLine(i, { mode: e.target.checked })} />
                      ) : <span className="sc2-muted">лаб.</span>}
                    </td>
                  )}
                  {raceId === 'protoss' && (
                    <td>
                      {s.unit.warpTime ? (
                        <input type="checkbox" checked={lines[i].mode} onChange={(e) => updateLine(i, { mode: e.target.checked })} />
                      ) : <span className="sc2-muted">—</span>}
                    </td>
                  )}
                  {raceId === 'zerg' && (
                    <td>
                      <input
                        className="sc2-count-input"
                        type="number" min="0" max="60"
                        value={lines[i].count}
                        onChange={(e) => updateLine(i, { count: Number(e.target.value) })}
                      />
                    </td>
                  )}
                  <td><strong>{formatNum(s.rate, 1)}</strong></td>
                  <td>{formatNum(s.minerals)}</td>
                  <td>{s.gas > 0 ? formatNum(s.gas) : '—'}</td>
                  <td>
                    <button className="sc2-remove-btn" onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="sc2-add-row">
            <select value={addUnitId} onChange={(e) => setAddUnitId(e.target.value)}>
              <option value="">+ Добавить юнит…</option>
              {availableUnits
                .filter((u) => !lines.some((l) => l.unitId === u.id))
                .map((u) => <option key={u.id} value={u.id}>{u.ru} ({u.name})</option>)}
            </select>
            <button className="sc2-add-btn" onClick={addLine} disabled={!addUnitId}>Добавить</button>
          </div>
        </div>

        <div className="sc2-panel">
          <h3>Баланс: доход vs расход</h3>

          <UsageBar label="💎 Минералы" spend={totals.minerals} income={income.minerals} />
          <UsageBar label="🟢 Газ" spend={totals.gas} income={income.gas} />
          {raceId === 'zerg' && (
            <UsageBar label="🥚 Личинки" spend={totals.larvae} income={larvaeAvailable} digits={1} />
          )}

          <div className={`sc2-verdict ${v.cls}`}>{v.text}</div>

          <table className="sc2-table compact">
            <tbody>
              <tr>
                <td>Расход лимита</td>
                <td>{formatNum(totals.supply, 1)}/мин ≈ {formatNum(totals.supply / 8, 1)} {raceId === 'zerg' ? 'владык' : raceId === 'terran' ? 'депо' : 'пилонов'}/мин</td>
              </tr>
              <tr>
                <td>Остаток дохода</td>
                <td>
                  💎 {formatNum(income.minerals - totals.minerals)}/мин · 🟢 {formatNum(income.gas - totals.gas)}/мин
                </td>
              </tr>
            </tbody>
          </table>
          <p className="sc2-note">
            Правило: держите расход на уровне 80–100% дохода. Излишек минералов → новые производственные здания или база. Излишек газа → более дорогие (газовые) юниты или апгрейды.
          </p>
        </div>
      </div>
    </div>
  );
}

function UsageBar({ label, spend, income, digits = 0 }) {
  const pct = income > 0 ? (spend / income) * 100 : (spend > 0 ? 200 : 0);
  const color = pct > 105 ? '#dc3545' : pct >= 80 ? '#28a745' : '#ffc107';
  return (
    <div className="sc2-usage">
      <div className="sc2-usage-head">
        <span>{label}</span>
        <span>{formatNum(spend, digits)} / {formatNum(income, digits)} в мин ({income > 0 ? Math.round(pct) : '∞'}%)</span>
      </div>
      <div className="sc2-progress">
        <div className="sc2-progress-fill" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
      </div>
    </div>
  );
}

export default ProductionPlanner;
